import { c as _c } from "react/compiler-runtime";
import { ForwardingsPerProject } from 'api-graphql-types';
import celebrationSvg from 'assets/images/fundraising-event/celebration.svg';
import { formatCents } from 'betterplace-utils';
import classNames from 'classnames';
import { Card, Heading, LayoutCluster, LayoutStack, WaveyWrapper } from 'design-system/client';
import Image, { StaticImageData } from 'next/image';
import { useTranslations, useLocale } from 'next-intl';
import styles from './ForwardingOverview.module.css';
import TextLink from '@/components/TextLink';
import { RailsRoutes } from '@/helpers/utils';
function ForwardingOverview(t0) {
  const $ = _c(49);
  const {
    completeForwardingsPerProject,
    forwardedAmountInCents,
    publicFaceName,
    receivedAmountInCents
  } = t0;
  const locale = useLocale();
  const fallbackLocale = locale === "de" ? "en" : "de";
  const t = useTranslations("nextjs.receiver_pages.fundraising_event.forwarding_overview");
  const remainingAmountInCents = receivedAmountInCents - forwardedAmountInCents;
  let t1;
  if ($[0] !== t) {
    t1 = t("celebration_image_alt");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <Image className={styles.celebrationImage} src={celebrationSvg as StaticImageData} alt={t1} />;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t) {
    t3 = t("title");
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <Heading level="heading-sm" as="h2" className={styles.heading}>{t3}</Heading>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t) {
    t5 = t("description");
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = <p className={styles.largeText}>{t5}</p>;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== completeForwardingsPerProject || $[13] !== fallbackLocale || $[14] !== locale) {
    t7 = completeForwardingsPerProject && completeForwardingsPerProject.map(forwarding => <LayoutCluster key={forwarding.receiver.id} className={styles.amountAndTextContainer}><div className={classNames(styles.importantText, styles.amount)}>{formatCents(locale, forwarding.amountInCents)}</div><TextLink prefetch={false} href={RailsRoutes.projectPage({
        locale,
        slug: forwarding.receiver.id
      })}>{forwarding.receiver.title?.[locale] ?? forwarding.receiver.title?.[fallbackLocale]}</TextLink></LayoutCluster>);
    $[12] = completeForwardingsPerProject;
    $[13] = fallbackLocale;
    $[14] = locale;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <hr className={styles.border} />;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = classNames(styles.importantText, styles.amountAndTextContainer);
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== forwardedAmountInCents || $[19] !== locale) {
    t10 = formatCents(locale, forwardedAmountInCents);
    $[18] = forwardedAmountInCents;
    $[19] = locale;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10) {
    t11 = <div className={styles.amount}>{t10}</div>;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== completeForwardingsPerProject.length || $[24] !== publicFaceName || $[25] !== t) {
    t12 = t("forwarded_so_far", {
      projectCount: completeForwardingsPerProject.length,
      publicFaceName
    });
    $[23] = completeForwardingsPerProject.length;
    $[24] = publicFaceName;
    $[25] = t;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12) {
    t13 = <p className={styles.summaryText}>{t12}</p>;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t11 || $[30] !== t13) {
    t14 = <LayoutCluster className={t9}>{t11}{t13}</LayoutCluster>;
    $[29] = t11;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== completeForwardingsPerProject.length || $[33] !== locale || $[34] !== publicFaceName || $[35] !== remainingAmountInCents || $[36] !== t) {
    t15 = remainingAmountInCents > 0 && <LayoutCluster className={styles.amountAndTextContainer}><div className={styles.amount}>{formatCents(locale, remainingAmountInCents)}</div><p className={styles.summaryText}>{t("still_to_be_forwarded", {
          projectCount: completeForwardingsPerProject.length,
          publicFaceName
        })}</p></LayoutCluster>;
    $[32] = completeForwardingsPerProject.length;
    $[33] = locale;
    $[34] = publicFaceName;
    $[35] = remainingAmountInCents;
    $[36] = t;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t14 || $[39] !== t15) {
    t16 = <LayoutStack space="200">{t8}<LayoutStack space="400" className={styles.bottomContainer}>{t14}{t15}</LayoutStack></LayoutStack>;
    $[38] = t14;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== t16 || $[42] !== t4 || $[43] !== t6 || $[44] !== t7) {
    t17 = <Card className={styles.card}><LayoutStack space="300" className={styles.topContainer}>{t4}{t6}{t7}{t16}</LayoutStack></Card>;
    $[41] = t16;
    $[42] = t4;
    $[43] = t6;
    $[44] = t7;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== t17 || $[47] !== t2) {
    t18 = <WaveyWrapper>{t2}{t17}</WaveyWrapper>;
    $[46] = t17;
    $[47] = t2;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  return t18;
}
export default ForwardingOverview;